import { type AccountKey, getAccountKeyForMailboxInfo, type MailboxInfo } from "owa-client-types";
import { getGuid } from "owa-guid";
import { getAccountFromMsal, setDefaultAnchorMailboxCookie } from "./utils/MsalAccounts";
import { getMsalInstance } from "./initializeMsalLibrary";
import { getTypeHintFromMailboxInfo } from "./getTypeHintFromMailboxInfo";
import { logStartCoreUsage, logStartUsage } from "owa-analytics-start";
import type { SilentRequest, AuthenticationResult } from "@azure/msal-browser-1p";
import { getScopes } from "./utils/getScopes";
import { getAuthority } from "./utils/getAuthority";
import { getSilentRedirectUri } from "./utils/getRedirectUri";
import { getMsalV5BridgeState } from "owa-tokenprovider-utils/lib/getMsalV5BridgeState";
import { version as msalVersion } from "@azure/msal-browser-1p";
import { getConfig } from "owa-service/lib/config";
import { setItem } from "owa-local-storage";
import { scrubForPii } from "owa-config";

enum State {
    Scheduled,
    Skip,
    Pending
}
const BackgroundRefreshState = new Map<AccountKey, State>();

export async function setupBackgroundTokenRefresh(mailboxInfo: MailboxInfo): Promise<void> {
    if (!getConfig().isFeatureEnabled("auth-msaljs-autoRefresh")) {
        return;
    }
    
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const state = BackgroundRefreshState.get(accountKey);
    if (state != undefined) {
        return;
    }
    
    BackgroundRefreshState.set(accountKey, State.Pending);
    
    const msalInstance = await getMsalInstance();
    const account = getAccountFromMsal(msalInstance, mailboxInfo);
    const signInState = account?.idTokenClaims?.signin_state;
    const isKmsiEnabled = signInState != undefined && (signInState as string[]).includes("kmsi");
    if (!isKmsiEnabled) {
        BackgroundRefreshState.set(accountKey, State.Skip);
        return;
    }
    
    const delayInMs = 4 * 60 * 60 * 1000 + getRandomDelayInMs(); // 4 hours + random number to avoid spikes
    
    setInterval(() => {
        forceRefreshTokens(mailboxInfo);
    }, delayInMs);
    
    BackgroundRefreshState.set(accountKey, State.Scheduled);
}

/**
 * Force renew Refresh Token in the background, to ensure valid tokens for longer period of time, and reduce delays for subsequent token requests.
 * Note: ssoSilent doesn't work when 3rd party cookies are blocked. In that case, renewing Refresh Token would fail silently and user session will continue using existing RT while it's valid.
 */
async function forceRefreshTokens(mailboxInfo: MailboxInfo): Promise<void> {
    const startTime = self.performance.now();
    
    const typeHint = getTypeHintFromMailboxInfo(mailboxInfo);
    const correlationId = getGuid();
    const msalInstance = await getMsalInstance();
    const account = getAccountFromMsal(msalInstance, mailboxInfo);
    let authResult: AuthenticationResult | null = null;
    let errorCode;
    let errorMessage;
    
    if (account) {
        const forceRefreshRequest: SilentRequest = {
            account,
            scopes: getScopes(typeHint),
            authority: getAuthority(typeHint),
            redirectUri: getSilentRedirectUri(),
            state: getMsalV5BridgeState(msalVersion),
            correlationId,
            forceRefresh: true, // Force refresh Access Token
            refreshTokenExpirationOffsetSeconds: 72000, // Force renew Refresh Token if it expires in 20 hrs
        };
        
        authResult = await msalInstance.acquireTokenSilent(forceRefreshRequest).catch(error => {
            errorCode = error.errorCode;
            errorMessage = error.errorMessage;
            return null;
        });
        
        if (authResult) {
            setDefaultAnchorMailboxCookie(authResult.account);
            
            if (authResult.expiresOn) {
                var tokenExpiry = authResult.expiresOn;
                setItem(self, "msalexp", tokenExpiry.toString());
            }
        }
    }
    const customData = {
        correlationId,
        accountType: typeHint,
        isAccountPresent: !!account,
        isSuccess: !!authResult,
        latency: self.performance.now() - startTime,
        pageVisible: self.document.visibilityState === "visible",
    };
    if (authResult) {
        logStartUsage("MsalBackgroundRefresh", mailboxInfo, customData);
    }
    else {
        logStartCoreUsage("MsalBackgroundRefreshFailure", mailboxInfo, {
            ...customData,
            errorCode,
            errorMessage: scrubForPii(errorMessage),
        });
    }
}

function getRandomDelayInMs(): number {
    const maxInMs = 10 * 60 * 1000; // 10 minutes
    return Math.floor(Math.random() * maxInMs);
}
